import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logger } from '@logger'
import { notify } from '@/core/toast'
import { initPlayer } from '@services/playerService'
import { setAuthenticationStatus, setSessionDetails } from '../store/slices/sessionSlice'
import { INTERNAL_CONSTANTS } from '@constants/internalConstants'
import { setFeatureToggles, setStakeConfigurations } from '../store/slices/configurationsSlice'
import { gameConfigurations } from '@configurations/gameConfigurations'
import { setWallet } from '../store/slices/walletSlice'

const LOGGER_KEY = 'Player Session Hook'

export const usePlayerSession = () => {
  const dispatch = useDispatch()

  const { token, gameCode } = useSelector((state) => state.launchParams.params)

  useEffect(() => {
    if (!token) {
      logger.error(LOGGER_KEY, 'Token is Required!')
      return
    }

    if (!gameCode) {
      logger.error(LOGGER_KEY, 'GameCode is Required!')
      return
    }

    // Stable id so the loading toast resolves in place (loading -> success/error).
    const SESSION_TOAST_ID = 'player-session'

    const init = async () => {
      dispatch(setAuthenticationStatus(INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.PENDING))
      notify('loading', {
        id: SESSION_TOAST_ID,
        title: 'Connecting',
        sub: 'Starting your session…',
      })
      logger.info(LOGGER_KEY, 'Initializing player session', { token, gameCode })

      try {
        const initData = await initPlayer({ token, gameCode })

        dispatch(setSessionDetails(initData))
        dispatch(
          setWallet({
            balance: initData?.balance || 0,
            currencyCode: initData?.operator?.currency,
          })
        )
        dispatch(setAuthenticationStatus(INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.SUCCEEDED))
        dispatch(
          setStakeConfigurations({
            minimumStakeAmount:
              initData?.stakeControls?.minimumStake ||
              gameConfigurations.defaultStakeConfigurations.minimumStake,
            maximumStakeAmount:
              initData?.stakeControls?.maximumStake ||
              gameConfigurations.defaultStakeConfigurations.maximumStake,
            defaultStakeAmount:
              initData?.stakeControls?.defaultStake ||
              gameConfigurations.defaultStakeConfigurations.defaultStake,
            step:
              initData?.stakeControls?.step || gameConfigurations.defaultStakeConfigurations.step,
            presetStakes:
              initData?.stakeControls?.presetBets ||
              gameConfigurations.defaultStakeConfigurations.presetStakes,
          })
        )
        dispatch(
          setFeatureToggles({
            jackpot: initData?.features?.jackpot || false,
            tournaments: initData?.features?.tournament || false,
            buyBonus: initData?.features?.buyBonus || false,
            freebets: initData?.features?.freebets || false,
            missions: initData?.features?.missions || false,
            chat: initData?.features?.chat || false,
            // Lets the per-game overrides in `featureOverrides` force a feature on
            // for specific games (chat on Defender / Howzzat).
            gameCode,
          })
        )

        notify('success', {
          id: SESSION_TOAST_ID,
          title: 'Connected',
          sub: 'Session ready',
        })
        logger.success(LOGGER_KEY, 'Player session initialized successfully', initData)
      } catch (err) {
        dispatch(setAuthenticationStatus(INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.FAILED))
        notify('error', {
          id: SESSION_TOAST_ID,
          title: 'Connection failed',
          sub: err?.message,
        })
        logger.error(LOGGER_KEY, 'Failed to initialize player session', err)
      }
    }

    init()
  }, [token, gameCode, dispatch])
}
